/**
 * OpenAI API クライアントコア
 * apiRequest（リトライ付きHTTPクライアント）、extractRateLimitInfo、ユーティリティ関数
 */

import type { RateLimitInfo, ApiResponse } from './types';
import { parseRetryAfter, retryDelayMs, waitForRetry, MAX_PROVIDER_WAIT_MS } from '../http-retry';

// Route all browser traffic through same-origin proxy to avoid CORS blocks.
export const BASE_URL = import.meta.env.VITE_OPENAI_API_BASE || '/api/openai/v1/organization';

export const padNumber = (value: number) => value.toString().padStart(2, '0');

export const formatUtcDate = (date: Date) => {
  return `${date.getUTCFullYear()}-${padNumber(date.getUTCMonth() + 1)}-${padNumber(date.getUTCDate())}`;
};

export const formatUtcHourKey = (date: Date) => {
  return `${formatUtcDate(date)} ${padNumber(date.getUTCHours())}:00:00`;
};

// Retry configuration for transient API errors
export const MAX_API_RETRIES = 3;
export const INITIAL_RETRY_DELAY_MS = 1000;
export const MAX_RETRY_DELAY_MS = 60_000;
export const RETRYABLE_STATUS_CODES = new Set([429, 500, 502, 503, 504]);

/**
 * レスポンスヘッダーからレートリミット情報を抽出
 */
export const extractRateLimitInfo = (headers: Headers): RateLimitInfo | undefined => {
  const remaining = headers.get('x-ratelimit-remaining-requests');
  if (remaining === null) return undefined;
  return {
    limitRequests: Number(headers.get('x-ratelimit-limit-requests')) || undefined,
    remainingRequests: Number(remaining) || 0,
    resetRequests: headers.get('x-ratelimit-reset-requests') || undefined,
    limitTokens: Number(headers.get('x-ratelimit-limit-tokens')) || undefined,
    remainingTokens: Number(headers.get('x-ratelimit-remaining-tokens')) || undefined,
    resetTokens: headers.get('x-ratelimit-reset-tokens') || undefined,
  };
};

/**
 * APIリクエストヘルパー（リトライ・Retry-Afterヘッダー対応）
 */
export const apiRequest = async <T>(
  endpoint: string,
  apiKey: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> => {
  let lastError: string = 'Unknown error occurred';

  for (let attempt = 0; attempt <= MAX_API_RETRIES; attempt++) {
    if (options.signal?.aborted) return { success: false, error: 'Request cancelled' };
    const timeoutSignal = AbortSignal.timeout(30_000);
    try {
      const response = await fetch(`${BASE_URL}${endpoint}`, {
        ...options,
        signal: options.signal ? AbortSignal.any([options.signal, timeoutSignal]) : timeoutSignal,
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      const rateLimits = extractRateLimitInfo(response.headers);

      if (response.ok) {
        const data = await response.json();
        return { success: true, data, rateLimits };
      }

      // Check if retryable
      if (RETRYABLE_STATUS_CODES.has(response.status) && attempt < MAX_API_RETRIES) {
        const delayMs = retryDelayMs(parseRetryAfter(response.headers.get('retry-after')), INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt));
        if (delayMs > MAX_PROVIDER_WAIT_MS) {
          return { success: false, error: `Retry deferred: provider requires waiting ${Math.ceil(delayMs / 1000)} seconds`, rateLimits };
        }
        console.warn(
          `[OpenAI API] ${response.status} on ${endpoint}, retrying in ${Math.round(delayMs)}ms (attempt ${attempt + 1}/${MAX_API_RETRIES})`
        );
        await waitForRetry(delayMs, options.signal);
        continue;
      }

      // Non-retryable error or exhausted retries
      const errorText = await response.text();
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.error?.message || errorMessage;
      } catch {
        // JSONパースエラーの場合は元のメッセージを使用
      }

      return { success: false, error: errorMessage, rateLimits };
    } catch (error) {
      if (options.signal?.aborted) return { success: false, error: 'Request cancelled' };
      if (timeoutSignal.aborted) return { success: false, error: 'Request timed out after 30 seconds' };
      lastError = error instanceof Error ? error.message : 'Unknown error occurred';
      if (attempt < MAX_API_RETRIES) {
        const delayMs = INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt) * (0.75 + Math.random() * 0.5);
        console.warn(
          `[OpenAI API] Network error on ${endpoint}, retrying in ${Math.round(delayMs)}ms (attempt ${attempt + 1}/${MAX_API_RETRIES})`
        );
        await new Promise(resolve => setTimeout(resolve, delayMs));
        continue;
      }
    }
  }

  return { success: false, error: lastError };
};
